import React, { useCallback, useEffect, useRef, useState } from 'react'
import { probeHealth } from '../apiClient'
import './MaintenanceNotice.css'

// Shown instead of the vendor form when the Function App is unreachable.
// Ported from Documents/vendor-ach-maintenance.html.

// Replace with the real vendor support address before deploying.
const SUPPORT_EMAIL = 'OHAAccounting@oceansideca.org'

const FIRST_AUTO_CHECK_MS = 60 * 1000
const MAX_AUTO_CHECK_MS = 5 * 60 * 1000
const MAX_AUTO_CHECKS = 10
const STILL_DOWN = 'Still finishing up — thanks for your patience!'

const MaintenanceNotice = ({ onRecovered }) => {
    const [checking, setChecking] = useState(false)
    const [message, setMessage] = useState('')
    const [lastChecked, setLastChecked] = useState(() => new Date())

    // Keep the latest callback in a ref so the auto-check loop doesn't restart when the parent re-renders.
    const onRecoveredRef = useRef(onRecovered)
    onRecoveredRef.current = onRecovered

    const checkNow = useCallback(async () => {
        setChecking(true)
        setMessage('Checking...')
        const healthy = await probeHealth()
        setLastChecked(new Date())
        setChecking(false)

        if (healthy) {
            setMessage("We're back! Returning to the form...")
            setTimeout(() => onRecoveredRef.current(), 1200)
        } else {
            setMessage(STILL_DOWN)
        }
        return healthy
    }, [])

    // Automatic checks back off (1 min, doubling, capped at 5 min) and stop after MAX_AUTO_CHECKS.
    // The button still works after that.
    useEffect(() => {
        let active = true
        let attempts = 0
        let delay = FIRST_AUTO_CHECK_MS
        let timerId

        const schedule = () => {
            if (attempts >= MAX_AUTO_CHECKS) return
            timerId = setTimeout(async () => {
                attempts += 1
                const healthy = await checkNow()
                if (!active || healthy) return
                delay = Math.min(delay * 2, MAX_AUTO_CHECK_MS)
                schedule()
            }, delay)
        }

        schedule()
        return () => {
            active = false
            clearTimeout(timerId)
        }
    }, [checkNow])

    return (
        <div className="maint-notice">
            <img className="maint-logo" src="logo3.png" alt="City of Oceanside" />

            <div className="maint-card">
                <div className="maint-badge"><span className="maint-dot"></span> Scheduled upgrade in progress</div>

                <div className="maint-icon-wrap">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="3"></circle>
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                    </svg>
                </div>

                <h1>We're making this page better</h1>
                <p className="maint-lead">
                    Our vendor ACH update portal is temporarily paused while we roll out a
                    security and reliability upgrade behind the scenes. This is routine
                    maintenance — nothing on your end needs attention.
                </p>

                <div className="maint-info">
                    <strong>What this means for you:</strong> Your previously submitted
                    details are safe and untouched. Once the upgrade is complete,
                    you'll be able to update your ACH information here as usual — no new
                    account or re-registration needed.
                    <br /><br />
                    <strong>Expected back online:</strong> Later today. Please check back
                    shortly, or use the button below and we'll let you know as soon as
                    we're ready.
                </div>

                <div className="maint-actions">
                    <button
                        className="maint-retry"
                        type="button"
                        disabled={checking}
                        onClick={() => checkNow()}
                    >
                        Check availability
                    </button>
                    <div className="maint-status" role="status" aria-live="polite">{message}</div>
                </div>

                <div className="maint-contact">
                    Need to update ACH details urgently? Contact our vendor support team at{' '}
                    <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                    {' '}and we'll process it manually in the meantime.
                </div>

                <footer className="maint-timestamp">
                    {lastChecked ? `Last checked: ${lastChecked.toLocaleTimeString()}` : ''}
                </footer>
            </div>
        </div>
    )
}

export default MaintenanceNotice
